import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import StickyHeader from "@/components/StickyHeader";
import HeroSection from "@/components/HeroSection";
const ServicesCalculator = lazy(() => import("@/components/ServicesCalculator"));
const AssortmentSection = lazy(() => import("@/components/AssortmentSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const ProductShowcase = lazy(() => import("@/components/ProductShowcase"));
const ContactSection = lazy(() => import("@/components/ContactSection"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const Testimonials = lazy(() => import("@/components/Testimonials"));

const Footer = lazy(() => import("@/components/Footer"));
const ScrollToTop = lazy(() => import("@/components/ScrollToTop"));

const SectionFallback = ({ minHeight = 400 }: { minHeight?: number }) => (
  <div style={{ minHeight }} aria-hidden="true" />
);

const productListJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Varuautomater från AdiStore",
  description: "Utforska vårt sortiment av varuautomater för företag och kontor.",
  numberOfItems: 3,
  // Startsidan visar inga priser per modell, därför pekar listan bara vidare till
  // respektive produktsida. Product/Offer-datat bor på modellsidan, där priset
  // faktiskt syns för besökaren (Googles krav på synligt innehåll).
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Kompakt Varuautomat",
      url: "https://www.adistore.se/automater/kompakt-varuautomat",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Classic Varuautomat",
      url: "https://www.adistore.se/automater/classic-varuautomat",
    },
    {
      "@type": "ListItem",
      position: 3,
      name: "Premium Varuautomat",
      url: "https://www.adistore.se/automater/premium-varuautomat",
    },
  ],
};

const serviceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Uthyrning av varuautomater med fullservice",
  serviceType: "Uthyrning av varuautomater",
  provider: {
    "@type": "Organization",
    name: "AdiStore",
    legalName: "Adikon Sverige AB",
    url: "https://www.adistore.se",
    telephone: "+46706291999",
    email: "info@adistore.se",
  },
  areaServed: { "@type": "AdministrativeArea", name: "Skåne län" },
  description:
    "Vi hyr ut varuautomater med kyld färdigmat, snacks och dryck till företag och kontor i Skåne. Leverans, installation, påfyllning och service ingår.",
  url: "https://www.adistore.se/",
};

const Index = () => {
  return (
    <div className="dark min-h-screen bg-background">
      <Helmet>
        <title>Varuautomater för företag i Skåne – hyr med fullservice | AdiStore</title>
        <meta
          name="description"
          content="Hyr varuautomat till kontor, lager och gym i Malmö, Lund, Helsingborg och hela Skåne. Kyld färdigmat, snacks och dryck med fullservice. Begär offert idag!"
        />
        <link rel="canonical" href="https://www.adistore.se/" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta property="og:title" content="Varuautomater för företag i Skåne – hyr med fullservice | AdiStore" />
        <meta
          property="og:description"
          content="Vi levererar och installerar varuautomater med kyld färdigmat, snacks och dryck till arbetsplatser i hela Skåne."
        />
        <meta property="og:url" content="https://www.adistore.se/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(productListJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </Helmet>
      <StickyHeader />
      <main>
        <HeroSection />
        <Suspense fallback={<SectionFallback minHeight={500} />}><ProductShowcase /></Suspense>
        <Suspense fallback={<SectionFallback minHeight={700} />}><ServicesCalculator /></Suspense>
        <Suspense fallback={<SectionFallback minHeight={500} />}><AssortmentSection /></Suspense>
        <Suspense fallback={<SectionFallback minHeight={500} />}><FAQSection /></Suspense>
        <Suspense fallback={<SectionFallback minHeight={600} />}><ContactSection /></Suspense>
        <Suspense fallback={<SectionFallback minHeight={400} />}><HowItWorks /></Suspense>
        <Suspense fallback={<SectionFallback minHeight={400} />}><Testimonials /></Suspense>
        
      </main>
      <Suspense fallback={<SectionFallback minHeight={200} />}><Footer /></Suspense>
      <Suspense fallback={null}><ScrollToTop /></Suspense>
    </div>
  );
};

export default Index;
